/* ============================================
   HERO SECTION COMPONENT
   The Creative Orchestra - GEMINI Framework
   Main hero section combining all landing elements
   ============================================ */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, ArrowDown, Sparkles, Volume2, VolumeX } from 'lucide-react';
import MilkyWayBackground from './MilkyWayBackground';
import GeminiConstellation, { GeminiText } from './GeminiConstellation';
import AudioSpectrum from './AudioSpectrum';
import OrbitalSystem, { MiniOrbitalSystem } from './OrbitalSystem';
import { useAudio } from '@context/AudioContext';
import { SITE_INFO } from '@utils/constants';

/**
 * Hero Section Component
 * The complete landing page hero with:
 * - Milky Way background
 * - GEMINI constellation/title
 * - Audio spectrum visualizer
 * - Orbital system with work orbs
 * - Call-to-action buttons
 */
const HeroSection = ({ className = '' }) => {
  const [isMobile, setIsMobile] = useState(false);
  const { isMuted, toggleMute, isLoaded, hasInteracted } = useAudio();

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <section className={`relative min-h-screen overflow-hidden ${className}`}>
      {/* Background */}
      <MilkyWayBackground starCount={isMobile ? 100 : 200} />

      {/* Content Container */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4">
        
        {/* Top Section - Title & Tagline */}
        <motion.div
          className="text-center mb-8 md:mb-12"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {/* GEMINI Title */}
          {isMobile ? (
            <GeminiText className="mb-4" />
          ) : (
            <div className="flex justify-center mb-6">
              <GeminiConstellation size="large" />
            </div>
          )}

          {/* Tagline */}
          <motion.p
            className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mt-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.6 }}
          >
            A Designer's Quest to Humanize Artificial Intelligence
          </motion.p>

          {/* Subtitle */}
          <motion.p
            className="text-sm md:text-base text-gray-500 mt-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 0.6 }}
          >
            PhD Thesis • {SITE_INFO.author} • {SITE_INFO.institution}
          </motion.p>
        </motion.div>

        {/* Audio Spectrum */}
        <motion.div
          className="w-full max-w-lg mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 0.8 }}
        >
          <AudioSpectrum 
            barCount={isMobile ? 20 : 32} 
            height={60}
            colorScheme="gradient"
          />
        </motion.div>

        {/* Orbital System */}
        <motion.div
          className="relative mb-12"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 1, ease: 'easeOut' }}
        >
          {isMobile ? (
            <MiniOrbitalSystem />
          ) : (
            <OrbitalSystem />
          )}
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row items-center gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2, duration: 0.6 }}
        >
          <Link to="/novel">
            <motion.button
              className="flex items-center gap-2 px-6 py-3 rounded-full
                bg-gradient-to-r from-indigo-500 to-purple-500
                text-white font-medium
                hover:from-indigo-400 hover:to-purple-400
                transition-all duration-300"
              whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(129, 140, 248, 0.5)' }}
              whileTap={{ scale: 0.95 }}
            >
              <BookOpen className="w-5 h-5" />
              Read the Novel
            </motion.button>
          </Link>

          <Link to="/about">
            <motion.button
              className="flex items-center gap-2 px-6 py-3 rounded-full
                bg-white/5 border border-white/20
                text-white font-medium
                hover:bg-white/10 hover:border-white/30
                transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Sparkles className="w-5 h-5" />
              About the Research
            </motion.button>
          </Link>
        </motion.div>

        {/* Audio prompt (if not interacted) */}
        <AnimatePresence>
          {isLoaded && !hasInteracted && (
            <motion.div
              className="absolute bottom-24 left-1/2 -translate-x-1/2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ delay: 3, duration: 0.5 }}
            >
              <motion.button
                onClick={toggleMute}
                className="flex items-center gap-2 px-4 py-2 rounded-full
                  bg-white/5 border border-white/10 text-gray-400 text-sm
                  hover:bg-white/10 hover:text-white transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {isMuted ? (
                  <>
                    <VolumeX className="w-4 h-4" />
                    Click for ambient music
                  </>
                ) : (
                  <>
                    <Volume2 className="w-4 h-4" />
                    Music enabled
                  </>
                )}
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.5, duration: 0.5 }}
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            className="flex flex-col items-center gap-2 text-gray-500"
          >
            <span className="text-xs tracking-wider">Scroll to explore</span>
            <ArrowDown className="w-4 h-4" />
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom gradient fade */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
        style={{
          background: 'linear-gradient(to top, rgba(10, 10, 15, 1) 0%, transparent 100%)',
        }}
      />
    </section>
  );
};

/**
 * Hero Section with additional content sections below
 */
export const HeroWithIntro = () => {
  return (
    <>
      <HeroSection />
      
      {/* Framework Introduction */}
      <section className="relative z-10 py-24 px-4 bg-[#0a0a0f]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            className="text-3xl md:text-4xl font-display font-bold text-white mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            What is GEMINI?
          </motion.h2>
          
          <motion.p
            className="text-lg text-gray-400 mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <strong className="text-white">G</strong>eneration, 
            <strong className="text-white"> E</strong>valuation, 
            <strong className="text-white"> M</strong>anagement, 
            <strong className="text-white"> IN</strong>spiration for <strong className="text-white">I</strong>deation
          </motion.p>

          <motion.p
            className="text-gray-400 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            A holistic ecosystem of AI systems designed to transform the design process 
            from passive computer-aided workflows to dynamic, agentic human-AI partnerships. 
            The designer is not a solitary craftsman but the conductor of an intelligent orchestra.
          </motion.p>
        </div>
      </section>
    </>
  );
};

export default HeroSection;
